/**
 * Billing Page — Central Handoff
 *
 * BeHeard does NOT own billing. Subscriptions, prices and credits are managed
 * by Central (LevelUp). This page shows, read-only, what GET /billing/status
 * reports (`@beheard/billing-contract`):
 *   - the plan tier BeHeard applies (its capacity tier),
 *   - Central's subscription, or "unavailable",
 *   - Central credits, or "Unavailable" (never a guessed 0),
 *   - the monthly usage allowances BeHeard enforces (local safety limits —
 *     not credits, not purchasable).
 * When the status cannot be read it says so: "Billing status temporarily
 * unavailable" — never a default "Free".
 *
 * Route: /billing, /settings/billing
 */

import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  BILLING_STATUS_UNAVAILABLE_MESSAGE,
  useCreatePortalSessionMutation,
  useGetBillingStatusQuery,
} from '../store/api/billingApi';
import { allowanceText, allowanceView, creditBalanceText, planNameOf } from '../utils/billingStatusView';
import { BILLING_CAPACITY_TIER_LABEL, BILLING_CAPACITY_TIER_NOTE } from '@beheard/billing-contract';
import { CENTRAL_URL } from '../config/centralAuth';
import toast from 'react-hot-toast';

const POOLS = [
  { key: 'practice', label: 'Practice', tone: 'text-blue-600 bg-blue-50' },
  { key: 'coaching', label: 'AI voice coaching', tone: 'text-purple-600 bg-purple-50' },
  { key: 'listen', label: 'Full listens', tone: 'text-green-600 bg-green-50' },
] as const;

export function BillingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { data: billingStatus, isLoading, isError, refetch } = useGetBillingStatusQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [createPortalSession, { isLoading: isOpeningPortal }] = useCreatePortalSessionMutation();

  if (!isAuthenticated) {
    navigate('/login?returnTo=/billing');
    return null;
  }

  const handleManageBilling = async () => {
    try {
      const result = await createPortalSession().unwrap();
      window.location.href = result.portalUrl;
    } catch {
      // Fallback: send to Central directly
      if (CENTRAL_URL) {
        window.open(`${CENTRAL_URL}/billing`, '_blank');
      } else {
        toast.error('Unable to open billing portal. Please try again.');
      }
    }
  };

  const planName = planNameOf(billingStatus);
  const subscription = billingStatus?.subscription;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="mt-1 text-gray-500">
          Your billing and subscription are managed by LevelUp Central.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      ) : isError || !billingStatus || !planName ? (
        <div className="card" data-testid="billing-status-unavailable">
          <div className="card-body space-y-3 text-center">
            <h2 className="font-semibold text-gray-900">{BILLING_STATUS_UNAVAILABLE_MESSAGE}</h2>
            <p className="text-sm text-gray-500">
              We couldn't read your plan just now. Nothing about your subscription has changed.
            </p>
            <button onClick={() => refetch()} className="btn-secondary">
              Try again
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current Plan (read-only) */}
          <div className="card">
            <div className="card-header flex justify-between items-center">
              {/* The commercial plan is Central's subscription only. */}
              <h2 className="font-semibold text-gray-900">Subscription</h2>
              {subscription?.available && subscription.subscription ? (
                <span className="badge-primary">{subscription.subscription.status}</span>
              ) : null}
            </div>
            <div className="card-body space-y-4">
              {subscription?.available ? (
                <p className="text-sm text-gray-700" data-testid="billing-subscription-plan">
                  {subscription.subscription
                    ? <>LevelUp plan: <span className="font-medium">{subscription.subscription.planCode}</span></>
                    : 'No LevelUp subscription on file.'}
                </p>
              ) : (
                <p className="text-sm text-amber-700" data-testid="billing-subscription-unavailable">
                  Subscription details are temporarily unavailable from LevelUp.
                </p>
              )}
              {/* BeHeard's capacity tier: noncommercial, labelled as such. */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{BILLING_CAPACITY_TIER_LABEL}</p>
                <h3 className="text-2xl font-bold text-gray-900" data-testid="billing-plan-name">
                  {planName}
                </h3>
                <p className="text-xs text-gray-500" data-testid="billing-capacity-tier-note">{BILLING_CAPACITY_TIER_NOTE}</p>
              </div>

              {/* Central credits — a number only when Central reports one */}
              <div className="text-sm text-gray-600" data-testid="billing-credits">
                LevelUp credits:{' '}
                <span className="font-medium">{creditBalanceText(billingStatus, 'practice')}</span>
                <span className="text-gray-400"> — credits are managed in LevelUp</span>
              </div>

              {/* Local safety allowances — usage limits, not credits */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Monthly usage allowances</p>
                <div className="grid grid-cols-3 gap-4">
                  {POOLS.map((pool) => (
                    <div key={pool.key} className={`text-center p-3 rounded-lg ${pool.tone}`}>
                      <p className="text-sm font-bold" data-testid={`allowance-${pool.key}`}>
                        {allowanceText(allowanceView(billingStatus, pool.key)) ?? '—'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{pool.label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Allowances are usage limits that reset monthly. They are not credits and cannot be bought.
                </p>
              </div>
            </div>
          </div>

          {/* Central Handoff */}
          <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
            <div className="card-body flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">Manage Your Subscription</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Upgrade, downgrade, and view billing history — all managed securely in LevelUp.
                </p>
              </div>
              <button
                onClick={handleManageBilling}
                disabled={isOpeningPortal}
                className="btn-primary whitespace-nowrap"
              >
                {isOpeningPortal ? 'Opening...' : 'Manage in LevelUp'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
